import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { fileToScaledDataUrl } from "@/lib/image";
import {
  Building2,
  Save,
  Image as ImageIcon,
  Loader2,
  MapPin,
  ReceiptText,
  Landmark,
  FileText,
  Globe2,
  Trash2,
  Palette,
  Hash,
  Scale,
  CreditCard,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = {
  name: "",
  logo_url: "",
  tax_id: "",
  phone: "",
  email: "",
  website: "",
  address: "",
  postal_code: "",
  city: "",
  province: "",
  country: "España",
  iban: "",
  bic: "",
  bank_name: "",
  account_holder: "",
  currency: "EUR",
  default_tax: 21,
  tax_regime: "general",
  default_payment_method: "transferencia",
  payment_due_days: 30,
  invoice_prefix: "F-",
  quote_prefix: "P-",
  work_order_prefix: "OT-",
  report_prefix: "PT-",
  invoice_next_number: 1,
  quote_next_number: 1,
  work_order_next_number: 1,
  report_next_number: 1,
  trade_name: "",
  legal_name: "",
  primary_color: "#0066CC",
  secondary_color: "#5856D6",
  dark_logo_url: "",
  warranty_text: "",
  default_notes: "",
  legal_text: "",
  privacy_text: "",
  pdf_footer: "Gracias por confiar en nosotros.",
  payment_terms: "Pago a 30 días.",
};

function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="mb-5 flex items-start gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0066CC]/10">
        <Icon className="h-5 w-5 text-[#0066CC]" />
      </div>

      <div>
        <h2 className="font-display text-lg font-semibold text-[#1D1D1F]">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-0.5 text-sm text-[#86868B]">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}

function formatSequence(prefix, number) {
  const safeNumber = Number(number) || 1;
  return String(prefix || "") + String(safeNumber).padStart(6, "0");
}

export default function Settings() {
  const { user, setUser } = useAuth();

  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOrganization = async () => {
      try {
        const response = await api.get("/organization");
        const organization = response.data || {};

        setForm({
          ...EMPTY_FORM,
          ...organization,
          default_tax:
            organization.default_tax == null
              ? 21
              : organization.default_tax,
        });
      } catch (error) {
        toast.error(
          formatApiError(error) ||
            "No se pudieron cargar los datos de la empresa."
        );
      } finally {
        setLoading(false);
      }
    };

    loadOrganization();
  }, []);

  if (user && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  const updateField = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const onLogo = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Selecciona una imagen válida.");
      return;
    }

    try {
      const dataUrl = await fileToScaledDataUrl(file, 500, 0.85);

      setForm((current) => ({
        ...current,
        logo_url: dataUrl,
      }));
    } catch {
      toast.error("No se pudo cargar el logo.");
    } finally {
      event.target.value = "";
    }
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Escribe el nombre de la empresa.");
      return;
    }

    if (
      form.default_tax !== "" &&
      (Number(form.default_tax) < 0 || Number(form.default_tax) > 100)
    ) {
      toast.error("El IVA debe estar entre 0 y 100.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        tax_id: form.tax_id.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        website: form.website.trim(),
        address: form.address.trim(),
        postal_code: form.postal_code.trim(),
        city: form.city.trim(),
        province: form.province.trim(),
        country: form.country.trim(),
        iban: form.iban.trim(),
        bic: form.bic.trim(),
        bank_name: form.bank_name.trim(),
        account_holder: form.account_holder.trim(),
        trade_name: form.trade_name.trim(),
        legal_name: form.legal_name.trim(),
        invoice_prefix: form.invoice_prefix.trim(),
        quote_prefix: form.quote_prefix.trim(),
        work_order_prefix: form.work_order_prefix.trim(),
        report_prefix: form.report_prefix.trim(),
        warranty_text: form.warranty_text.trim(),
        default_notes: form.default_notes.trim(),
        legal_text: form.legal_text.trim(),
        privacy_text: form.privacy_text.trim(),
        pdf_footer: form.pdf_footer.trim(),
        payment_terms: form.payment_terms.trim(),
        default_tax: Number(form.default_tax) || 0,
        payment_due_days: Number(form.payment_due_days) || 0,
        invoice_next_number: Number(form.invoice_next_number) || 1,
        quote_next_number: Number(form.quote_next_number) || 1,
        work_order_next_number: Number(form.work_order_next_number) || 1,
        report_next_number: Number(form.report_next_number) || 1,
      };

      const response = await api.put("/organization", payload);
      const saved = response.data || payload;

      setForm((current) => ({
        ...current,
        ...saved,
      }));

      toast.success("Datos de empresa guardados.");

      if (user) {
        setUser({
          ...user,
          org_name: saved.name || payload.name,
        });
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const field = ({
    label,
    key,
    placeholder = "",
    type = "text",
    required = false,
    min,
    max,
    step,
  }) => (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
        {label}
      </label>

      <input
        data-testid={"org-" + key}
        type={type}
        value={form[key] ?? ""}
        onChange={(event) => updateField(key, event.target.value)}
        placeholder={placeholder}
        required={required}
        min={min}
        max={max}
        step={step}
        className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 outline-none transition-all focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
      />
    </div>
  );

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto h-7 w-7 animate-spin text-[#0066CC]" />
          <p className="mt-3 text-sm text-[#86868B]">
            Cargando empresa...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <PageHeader
        title="Empresa"
        subtitle="Configura los datos que aparecerán en partes, presupuestos y facturas."
        action={
          <button
            data-testid="save-org-top"
            type="button"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 rounded-full bg-[#0066CC] px-6 py-2.5 font-medium text-white shadow-[0_4px_12px_rgba(0,102,204,0.3)] transition-all hover:-translate-y-0.5 hover:bg-[#004499] disabled:opacity-60"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        }
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="mantix-card h-fit p-6">
          <SectionTitle
            icon={Building2}
            title="Identidad"
            subtitle="Logo e información principal."
          />

          <div className="flex flex-col items-center text-center">
            <div className="mb-4 flex h-36 w-36 items-center justify-center overflow-hidden rounded-3xl bg-[#F5F5F7]">
              {form.logo_url ? (
                <img
                  src={form.logo_url}
                  alt="Logo de la empresa"
                  className="h-full w-full object-contain"
                  data-testid="org-logo-preview"
                />
              ) : (
                <Building2
                  className="h-12 w-12 text-[#86868B]"
                  strokeWidth={1.5}
                />
              )}
            </div>

            <label className="cursor-pointer rounded-full bg-[#0066CC]/10 px-4 py-2 text-sm font-medium text-[#0066CC] transition-all hover:bg-[#0066CC]/15">
              <span className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                {form.logo_url ? "Cambiar logo" : "Subir logo"}
              </span>

              <input
                data-testid="org-logo-input"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={onLogo}
                className="hidden"
              />
            </label>

            {form.logo_url && (
              <button
                data-testid="org-logo-remove"
                type="button"
                onClick={() => updateField("logo_url", "")}
                className="mt-3 flex items-center gap-1.5 text-xs text-[#86868B] hover:text-[#FF3B30]"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Quitar logo
              </button>
            )}

            <p className="mt-4 text-xs leading-relaxed text-[#86868B]">
              Recomendado: PNG o JPG cuadrado, con fondo transparente y menos de
              2 MB.
            </p>
          </div>
        </aside>

        <main className="space-y-6">
          <section className="mantix-card p-6">
            <SectionTitle
              icon={Building2}
              title="Datos generales"
              subtitle="Información identificativa y de contacto."
            />

            <div className="space-y-4">
              {field({
                label: "Nombre de la empresa",
                key: "name",
                placeholder: "ClimaTech Servicios SL",
                required: true,
              })}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {field({
                  label: "Nombre comercial",
                  key: "trade_name",
                  placeholder: "ClimaTech",
                })}

                {field({
                  label: "Razón social",
                  key: "legal_name",
                  placeholder: "ClimaTech Servicios, S.L.",
                })}
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {field({
                  label: "CIF / NIF",
                  key: "tax_id",
                  placeholder: "B-12345678",
                })}

                {field({
                  label: "Teléfono",
                  key: "phone",
                  placeholder: "+34 900 000 000",
                  type: "tel",
                })}
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {field({
                  label: "Email",
                  key: "email",
                  placeholder: "info@empresa.es",
                  type: "email",
                })}

                {field({
                  label: "Página web",
                  key: "website",
                  placeholder: "https://www.empresa.es",
                  type: "url",
                })}
              </div>
            </div>
          </section>

          <section className="mantix-card p-6">
            <SectionTitle
              icon={MapPin}
              title="Dirección fiscal"
              subtitle="Se mostrará en documentos y comunicaciones."
            />

            <div className="space-y-4">
              {field({
                label: "Dirección",
                key: "address",
                placeholder: "C/ Mayor 10",
              })}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {field({
                  label: "Código postal",
                  key: "postal_code",
                  placeholder: "35500",
                })}

                {field({
                  label: "Ciudad",
                  key: "city",
                  placeholder: "Arrecife",
                })}

                {field({
                  label: "Provincia",
                  key: "province",
                  placeholder: "Las Palmas",
                })}

                {field({
                  label: "País",
                  key: "country",
                  placeholder: "España",
                })}
              </div>
            </div>
          </section>

          <section className="mantix-card p-6">
            <SectionTitle
              icon={ReceiptText}
              title="Facturación"
              subtitle="Impuestos, cobros y valores por defecto."
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                  Moneda
                </label>

                <select
                  data-testid="org-currency"
                  value={form.currency}
                  onChange={(event) =>
                    updateField("currency", event.target.value)
                  }
                  className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 outline-none focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                >
                  <option value="EUR">EUR (€)</option>
                  <option value="USD">USD ($)</option>
                  <option value="GBP">GBP (£)</option>
                </select>
              </div>

              {field({
                label: "IVA por defecto (%)",
                key: "default_tax",
                type: "number",
                min: 0,
                max: 100,
                step: 0.01,
              })}

              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                  Régimen fiscal
                </label>

                <select
                  data-testid="org-tax-regime"
                  value={form.tax_regime}
                  onChange={(event) =>
                    updateField("tax_regime", event.target.value)
                  }
                  className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 outline-none focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                >
                  <option value="general">Régimen general</option>
                  <option value="recargo_equivalencia">Recargo de equivalencia</option>
                  <option value="exento">Exento</option>
                  <option value="canarias_igic">IGIC (Canarias)</option>
                </select>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                  Forma de pago
                </label>

                <select
                  data-testid="org-payment-method"
                  value={form.default_payment_method}
                  onChange={(event) =>
                    updateField("default_payment_method", event.target.value)
                  }
                  className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 outline-none focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                >
                  <option value="transferencia">Transferencia</option>
                  <option value="domiciliacion">Domiciliación</option>
                  <option value="tarjeta">Tarjeta</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="bizum">Bizum</option>
                </select>
              </div>

              {field({
                label: "Vencimiento (días)",
                key: "payment_due_days",
                type: "number",
                min: 0,
                max: 365,
                step: 1,
              })}
            </div>
          </section>

          <section className="mantix-card p-6">
            <SectionTitle
              icon={Hash}
              title="Numeración"
              subtitle="Prefijos y siguiente número de cada documento."
            />

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
              {[
                ["Facturas", "invoice_prefix", "invoice_next_number"],
                ["Presupuestos", "quote_prefix", "quote_next_number"],
                ["Órdenes", "work_order_prefix", "work_order_next_number"],
                ["Partes", "report_prefix", "report_next_number"],
              ].map(([label, prefixKey, numberKey]) => (
                <div
                  key={prefixKey}
                  className="rounded-2xl bg-[#F5F5F7] p-4"
                >
                  <p className="mb-3 text-sm font-semibold text-[#1D1D1F]">
                    {label}
                  </p>

                  <div className="grid grid-cols-2 gap-3">
                    {field({
                      label: "Prefijo",
                      key: prefixKey,
                      placeholder: "F-",
                    })}

                    {field({
                      label: "Siguiente",
                      key: numberKey,
                      type: "number",
                      min: 1,
                      step: 1,
                    })}
                  </div>

                  <p className="mt-3 text-xs text-[#86868B]">
                    Vista previa:{" "}
                    <span className="font-mono font-medium text-[#1D1D1F]">
                      {formatSequence(form[prefixKey], form[numberKey])}
                    </span>
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mantix-card p-6">
            <SectionTitle
              icon={Landmark}
              title="Datos bancarios"
              subtitle="Opcionales. Se podrán mostrar en facturas."
            />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {field({
                label: "Banco",
                key: "bank_name",
                placeholder: "Nombre del banco",
              })}

              {field({
                label: "Titular de la cuenta",
                key: "account_holder",
                placeholder: "Razón social o titular",
              })}

              {field({
                label: "IBAN",
                key: "iban",
                placeholder: "ES00 0000 0000 0000 0000 0000",
              })}

              {field({
                label: "BIC / SWIFT",
                key: "bic",
                placeholder: "XXXXXXXX",
              })}
            </div>
          </section>

          <section className="mantix-card p-6">
            <SectionTitle
              icon={Palette}
              title="Marca"
              subtitle="Colores corporativos usados en documentos y comunicaciones."
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                  Color principal
                </label>
                <div className="flex items-center gap-3 rounded-xl bg-[#F5F5F7] px-4 py-3">
                  <input
                    type="color"
                    value={form.primary_color}
                    onChange={(event) =>
                      updateField("primary_color", event.target.value)
                    }
                    className="h-9 w-12 cursor-pointer rounded border-0 bg-transparent"
                  />
                  <input
                    value={form.primary_color}
                    onChange={(event) =>
                      updateField("primary_color", event.target.value)
                    }
                    className="min-w-0 flex-1 bg-transparent font-mono text-sm outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                  Color secundario
                </label>
                <div className="flex items-center gap-3 rounded-xl bg-[#F5F5F7] px-4 py-3">
                  <input
                    type="color"
                    value={form.secondary_color}
                    onChange={(event) =>
                      updateField("secondary_color", event.target.value)
                    }
                    className="h-9 w-12 cursor-pointer rounded border-0 bg-transparent"
                  />
                  <input
                    value={form.secondary_color}
                    onChange={(event) =>
                      updateField("secondary_color", event.target.value)
                    }
                    className="min-w-0 flex-1 bg-transparent font-mono text-sm outline-none"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="mantix-card p-6">
            <SectionTitle
              icon={FileText}
              title="Textos de documentos"
              subtitle="Mensajes que aparecerán en presupuestos y facturas."
            />

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {[
                ["Condiciones de pago", "payment_terms", "Pago a 30 días."],
                ["Garantía", "warranty_text", "Garantía de 2 años según condiciones."],
                ["Observaciones por defecto", "default_notes", "Observaciones habituales del servicio."],
                ["Pie de página", "pdf_footer", "Gracias por confiar en nosotros."],
                ["Texto legal", "legal_text", "Información legal que aparecerá en los documentos."],
                ["Privacidad / RGPD", "privacy_text", "Información sobre protección de datos."],
              ].map(([label, key, placeholder]) => (
                <div key={key}>
                  <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                    {label}
                  </label>

                  <textarea
                    data-testid={"org-" + key}
                    value={form[key]}
                    onChange={(event) =>
                      updateField(key, event.target.value)
                    }
                    rows={4}
                    placeholder={placeholder}
                    className="w-full resize-none rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 outline-none transition-all focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                  />
                </div>
              ))}
            </div>
          </section>

          <div className="flex justify-end pb-4">
            <button
              data-testid="save-org"
              type="button"
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 rounded-full bg-[#0066CC] px-7 py-3 font-medium text-white shadow-[0_4px_12px_rgba(0,102,204,0.3)] transition-all hover:-translate-y-0.5 hover:bg-[#004499] disabled:opacity-60"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}

              {saving ? "Guardando..." : "Guardar configuración"}
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}